import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Tabs, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomElement } from '@/api/customElements';
import type { WorkflowStep, WorkflowStepType } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';

import { CustomElementTabContent } from './form-builder/customElements/CustomElementTabContent';
import { STEP_TYPE_META, type StepTypeMeta } from './stepTypes';

const { Text } = Typography;

type Props = {
  open: boolean;
  onCancel: () => void;
  /** Types that already exist on the workflow — singleton types are disabled when present. */
  existingSteps: WorkflowStep[];
  onPick: (type: WorkflowStepType) => void;
  /** Creates a new DynamicForm step pre-populated from a saved Step custom element. */
  onPickCustom: (el: CustomElement) => void;
};

const CARD_BASE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '18px 18px 18px 16px',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  background: '#fff',
  textAlign: 'left',
  width: '100%',
  transition:
    'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, background 160ms ease',
};

const ICON_TILE: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  flexShrink: 0,
  transition: 'transform 160ms ease',
};

const ARROW_CHIP_BASE: CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: '50%',
  fontSize: 12,
  color: '#fff',
  transition: 'opacity 160ms ease, transform 160ms ease, background 160ms ease',
};

/**
 * Step type picker. Each card carries a coloured icon tile, the type label, a 2-line
 * description, and a clickable affordance that lifts on hover. Singleton types that
 * already exist on the workflow render disabled with an "Already added" caption.
 */
export function AddStepModal({ open, onCancel, existingSteps, onPick, onPickCustom }: Props) {
  const { t } = useTranslation();
  const existingTypes = new Set(existingSteps.map((s) => s.type));

  return (
    <BmoModal
      open={open}
      onCancel={onCancel}
      title={t('workflow.steps.addModalTitle')}
      width={720}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
      ]}
    >
      <Tabs
        items={[
          {
            key: 'catalog',
            label: t('workflow.builder.palette.catalogTab'),
            children: (
              <>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 16, fontSize: 13, lineHeight: 1.4 }}
                >
                  {t('workflow.steps.addModalSubtitle')}
                </Text>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 14,
                  }}
                >
                  {STEP_TYPE_META.filter((meta) => meta.originatorAddable).map((meta) => {
                    const disabled = meta.singleton && existingTypes.has(meta.type);
                    return (
                      <StepTypeCard
                        key={meta.type}
                        meta={meta}
                        disabled={disabled}
                        onPick={onPick}
                      />
                    );
                  })}
                </div>
              </>
            ),
          },
          {
            key: 'custom',
            label: t('workflow.customElements.tabLabel'),
            children: <CustomElementTabContent types={['Step']} onPickStep={onPickCustom} />,
          },
        ]}
      />
    </BmoModal>
  );
}

function StepTypeCard({
  meta,
  disabled,
  onPick,
}: {
  meta: StepTypeMeta;
  disabled: boolean;
  onPick: (type: WorkflowStepType) => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const showHoverState = hover && !disabled;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(meta.type)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        ...CARD_BASE,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? '#f9fafb' : showHoverState ? meta.tintBg : '#fff',
        opacity: disabled ? 0.55 : 1,
        borderColor: showHoverState ? meta.tintFg : '#e5e7eb',
        boxShadow: showHoverState
          ? `0 6px 16px ${meta.tintBg}, 0 1px 3px rgba(0, 0, 0, 0.04)`
          : '0 1px 2px rgba(0, 0, 0, 0.03)',
        transform: showHoverState ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <span
        style={{
          ...ICON_TILE,
          background: meta.tintBg,
          color: meta.tintFg,
          transform: showHoverState ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {meta.icon()}
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          paddingRight: 32,
        }}
      >
        <Text strong style={{ fontSize: 15, color: '#111827' }}>
          {t(meta.labelKey)}
        </Text>
        <Text
          type="secondary"
          style={{
            fontSize: 12.5,
            lineHeight: 1.45,
            color: '#52606D',
          }}
        >
          {t(meta.descriptionKey)}
        </Text>
        {disabled && (
          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', marginTop: 4 }}>
            {t('workflow.steps.alreadyAdded')}
          </Text>
        )}
      </div>
      {/* "Add" affordance — small circular chip in the top-right that swaps from a plus
          (always visible) to an arrow (on hover). Pure visual hint; the whole card is
          the clickable target. */}
      {!disabled && (
        <span
          style={{
            ...ARROW_CHIP_BASE,
            background: meta.tintFg,
            opacity: showHoverState ? 1 : 0.7,
            transform: showHoverState ? 'translateX(2px)' : 'translateX(0)',
          }}
          aria-hidden
        >
          {showHoverState ? <ArrowRightOutlined /> : <PlusOutlined />}
        </span>
      )}
    </button>
  );
}
